import { useCallback, useEffect, useRef, useState } from "react";
import {
  ReactFlow, Background, Controls, MiniMap, MarkerType, ConnectionMode, Panel,
  Handle, Position, BaseEdge, EdgeLabelRenderer,
  useInternalNode, useNodesState, useEdgesState,
} from "@xyflow/react";
import "@xyflow/react/dist/style.css";
import { X, ArrowsClockwise, Trash, Question, CircleNotch, WarningCircle, CaretDown, CaretRight, FrameCorners } from "@phosphor-icons/react";
import { toast } from "@/components/ui/sonner";
import api from "@/lib/api";

const REL = { worse: { color: "#D9281C", text: "worsens" }, better: { color: "#24A148", text: "improves" }, enables: { color: "#0F62FE", text: "enables" } };
const CHANGE = "#8A3FFC";
const legacy = (r) => (["enables", "facilitates", "supports"].includes(r) ? "enables" : ["reduces", "resolves", "weakens", "increases", "strengthens", "balances", "better"].includes(r) ? "better" : ["worse", "better", "enables"].includes(r) ? r : "worse");
const relColor = (r) => (REL[legacy(r)] || REL.worse).color;
const relParts = (r) => { const k = legacy(r); return k === "enables" ? { verb: "enables", tail: "" } : { verb: "makes", tail: k === "better" ? "better" : "worse" }; };
function RelSentence({ subject, target, rel, boldSubject, boldTarget }) {
  const c = relColor(rel); const { verb, tail } = relParts(rel);
  return (
    <span>{boldSubject ? <strong>{subject}</strong> : subject} <span style={{ color: c }}>{verb}</span> {boldTarget ? <strong>{target}</strong> : target}{tail ? <> <span style={{ color: c }}>{tail}</span></> : null}</span>
  );
}
const RELATIONS = ["worse", "better", "enables"];
const CONF = ["low", "moderate", "high"];
const roleOf = (n) => (n.kind === "intervention" || n.kind === "solution") ? "strategy" : (n.kind === "problem" && n.parent) ? "subproblem" : "problem";

/* ---------------------------------------------------------------- nodes */
function SystemNode({ data }) {
  const { raw, role, tier, dim, active } = data;
  if (role === "region") return <div style={{ width: raw.size, height: raw.size, borderRadius: "50%", background: "rgba(15,23,42,0.05)", border: "1px dashed rgba(100,116,139,0.35)", pointerEvents: "none" }} />;
  const strat = role === "strategy";
  let cls, titleCls, descCls, badge = null;
  if (!strat) { cls = "bg-[#0d5c63] text-white"; titleCls = "text-white"; descCls = "text-white/70"; }
  else if (tier === "primary") { cls = "bg-[#0F62FE] text-white"; titleCls = "text-white"; descCls = "text-white/80"; badge = "Primary"; }
  else { cls = "bg-background border border-dashed border-[#0F62FE] text-[#0b57d0] dark:text-[#78A9FF]"; titleCls = "text-[#0b57d0] dark:text-[#78A9FF]"; descCls = "text-muted-foreground"; badge = "Then"; }
  const desc = (raw.summary || "").trim();
  return (
    <div className={`relative rounded-none px-3 py-2 shadow-sm transition-all duration-200 ${cls} ${active ? "ring-2 ring-offset-1 ring-foreground/60" : ""}`} style={{ width: strat ? 196 : 190, opacity: dim ? 0.12 : 1 }}>
      <Handle type="target" position={Position.Top} className="!h-1.5 !w-1.5 !min-w-0 !border-0 !bg-transparent" />
      <Handle type="source" position={Position.Bottom} className="!h-1.5 !w-1.5 !min-w-0 !border-0 !bg-transparent" />
      {badge && <span className="absolute -top-2 left-2 border border-[#0F62FE] bg-background px-1 py-px font-mono text-[7.5px] font-bold uppercase tracking-widest text-[#0F62FE]">{badge}</span>}
      <div className={`font-serif text-[14px] font-semibold leading-tight line-clamp-2 ${titleCls}`}>{raw.label}</div>
      {desc && <div className={`mt-1 text-[11px] leading-snug line-clamp-2 ${descCls}`}>{desc}</div>}
    </div>
  );
}
const nodeTypes = { sys: SystemNode };

/* ---------------------------------------------------------------- edges */
const centerOf = (n) => ({ x: n.internals.positionAbsolute.x + (n.measured?.width ?? 150) / 2, y: n.internals.positionAbsolute.y + (n.measured?.height ?? 44) / 2, w: n.measured?.width ?? 150, h: n.measured?.height ?? 44 });
const borderPoint = (c, tx, ty) => {
  const dx = tx - c.x, dy = ty - c.y;
  if (!dx && !dy) return { x: c.x, y: c.y };
  const s = 0.5 / Math.max(Math.abs(dx) / c.w, Math.abs(dy) / c.h);
  return { x: c.x + dx * s, y: c.y + dy * s };
};
function FloatingEdge({ id, source, target, markerEnd, style, data }) {
  const s = useInternalNode(source);
  const t = useInternalNode(target);
  const m = useInternalNode(data?.midTo || source);
  if (!s || !t) return null;
  const cs = centerOf(s), ct = centerOf(t);
  let tx, ty, sb;
  if (data?.midTo && m) { const cm = centerOf(m); tx = (ct.x + cm.x) / 2; ty = (ct.y + cm.y) / 2; sb = borderPoint(cs, tx, ty); }
  else { const tb = borderPoint(ct, cs.x, cs.y); tx = tb.x; ty = tb.y; sb = borderPoint(cs, ct.x, ct.y); }
  const path = `M ${sb.x},${sb.y} L ${tx},${ty}`;
  const dxl = tx - sb.x, dyl = ty - sb.y, ll = Math.hypot(dxl, dyl) || 1;
  const off = 13;
  const labelX = (sb.x + tx) / 2 + (-dyl / ll) * off, labelY = (sb.y + ty) / 2 + (dxl / ll) * off;
  return (
    <>
      <BaseEdge id={id} path={path} markerEnd={markerEnd} style={style} />
      {data?.showBadge && data?.sign ? (
        <EdgeLabelRenderer>
          <div style={{ position: "absolute", transform: `translate(-50%,-50%) translate(${labelX}px,${labelY}px)`, color: data.color, textShadow: "0 0 3px #fff, 0 0 3px #fff, 0 0 3px #fff, 0 0 3px #fff" }}
            className="pointer-events-none font-mono text-[17px] font-black leading-none">
            {data.sign}
          </div>
        </EdgeLabelRenderer>
      ) : null}
    </>
  );
}
const edgeTypes = { floating: FloatingEdge };

/* three connection types: influences (+/-), reinforces (+/-, filled badge), enables */
const GREEN = "#24A148", RED = "#D9281C", ENABLE = "#0F62FE";
function edgeSpec(e, role) {
  const rs = role(e.source), rt = role(e.target);
  if (rs === "strategy" && rt === "strategy") return { type: "enable", sign: null, filled: false, color: ENABLE, dash: "5 4", base: 0.5, w: 1.8 };
  if (e.group === "changes_link") { const plus = e.relation === "strengthens"; return { type: "reinforce", sign: plus ? "+" : "\u2212", filled: true, color: plus ? GREEN : RED, dash: undefined, base: 0.85, w: 3 }; }
  if (rs === "problem" && rt === "problem") {
    const plus = legacy(e.relation) === "better";
    if (e.loop === "reinforcing") return { type: "reinforce", sign: plus ? "+" : "\u2212", filled: true, color: plus ? GREEN : RED, dash: undefined, base: 0.8, w: 3 };
    return { type: "influence", sign: plus ? "+" : "\u2212", filled: false, color: plus ? GREEN : RED, dash: undefined, base: 0.7, w: 1.8 };
  }
  if (rs === "strategy" && rt === "problem") { const plus = legacy(e.relation) !== "worse"; return { type: "influence", sign: plus ? "+" : "\u2212", filled: false, color: plus ? GREEN : RED, dash: undefined, base: 0.78, w: 2.2 }; }
  const plus = legacy(e.relation) === "better";
  return { type: "influence", sign: plus ? "+" : "\u2212", filled: false, color: plus ? GREEN : RED, dash: undefined, base: 0.5, w: 1.4 };
}

/* ---------------------------------------------------------------- orbital layout */
function computeLayout(g) {
  const nodes = g.nodes || [];
  const edges = g.edges || [];
  const role = (id) => { const n = nodes.find((x) => x.id === id); return n ? roleOf(n) : null; };
  const problems = nodes.filter((n) => roleOf(n) === "problem");
  const strategies = nodes.filter((n) => roleOf(n) === "strategy");
  const pids = new Set(problems.map((n) => n.id));

  const improves = {}, incoming = {};
  strategies.forEach((s) => { improves[s.id] = 0; incoming[s.id] = 0; });
  edges.forEach((e) => {
    if (role(e.source) === "strategy" && pids.has(e.target) && e.group !== "changes_link") improves[e.source]++;
    if (role(e.source) === "strategy" && role(e.target) === "strategy") incoming[e.target]++;
  });
  const tier = {};
  strategies.forEach((s) => { tier[s.id] = incoming[s.id] === 0 ? "primary" : "secondary"; });

  // order problems to reduce crossing (greedy adjacency)
  const adj = {}; problems.forEach((p) => (adj[p.id] = new Set()));
  edges.forEach((e) => { if (pids.has(e.source) && pids.has(e.target)) { adj[e.source].add(e.target); adj[e.target].add(e.source); } });
  const deg = {}; problems.forEach((p) => (deg[p.id] = adj[p.id].size));
  const remaining = new Set(problems.map((p) => p.id));
  const order = [];
  if (problems.length) {
    let start = problems.slice().sort((a, b) => deg[b.id] - deg[a.id])[0].id;
    order.push(start); remaining.delete(start);
    while (remaining.size) {
      let best = null, bc = -1;
      remaining.forEach((id) => { const c = [...adj[id]].filter((x) => order.includes(x)).length; if (c > bc) { bc = c; best = id; } });
      order.push(best); remaining.delete(best);
    }
  }

  const N = Math.max(1, order.length);
  const Rin = 40 + 34 * N;
  const pos = {}, ang = {};
  order.forEach((id, i) => {
    const a = -Math.PI / 2 + (2 * Math.PI * i) / N;
    ang[id] = a;
    pos[id] = { x: Math.round(Rin * Math.cos(a) - 95), y: Math.round(Rin * Math.sin(a) - 34) };
  });

  const Rp = Rin + 170, Rs = Rin + 280;
  strategies.forEach((st, i) => {
    const targets = edges.filter((e) => e.source === st.id && pids.has(e.target)).map((e) => e.target);
    let a;
    if (targets.length) { const xs = targets.map((t) => Math.cos(ang[t])).reduce((s, v) => s + v, 0) / targets.length; const ys = targets.map((t) => Math.sin(ang[t])).reduce((s, v) => s + v, 0) / targets.length; a = Math.atan2(ys, xs); }
    else a = -Math.PI / 2 + (2 * Math.PI * i) / Math.max(1, strategies.length);
    const R = tier[st.id] === "primary" ? Rp : Rs;
    pos[st.id] = { x: Math.round(R * Math.cos(a) - 98), y: Math.round(R * Math.sin(a) - 34) };
  });
  const regionSize = 2 * (Rin + 105);
  return { pos, tier, ang, region: { x: -regionSize / 2, y: -regionSize / 2, size: regionSize } };
}

/* ---------------------------------------------------------------- collapsible */
function Section({ title, children, defaultOpen = false, plain = false }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="border-t border-border pt-2">
      <button onClick={() => setOpen((o) => !o)} className="flex w-full items-center justify-between gap-2 text-left">
        <span className={plain ? "text-[11.5px] font-semibold text-foreground" : "font-mono text-[10px] font-semibold uppercase tracking-widest text-muted-foreground"}>{title}</span>
        {open ? <CaretDown size={12} className="shrink-0 text-muted-foreground" /> : <CaretRight size={12} className="shrink-0 text-muted-foreground" />}
      </button>
      {open && <div className="mt-1.5">{children}</div>}
    </div>
  );
}

/* ---------------------------------------------------------------- component */
export default function SystemsMap({ sessionId, systemsMap, components = [], fill = false, onChanged }) {
  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);
  const [sel, setSel] = useState(null);
  const [explaining, setExplaining] = useState(false);
  const [explanation, setExplanation] = useState({});
  const graphRef = useRef(systemsMap);
  const layoutRef = useRef({ pos: {}, tier: {}, region: null });
  const focusRef = useRef(null);
  const hoverRef = useRef(null);
  const rfiRef = useRef(null);

  const focusView = useCallback((ids) => {
    const inst = rfiRef.current;
    if (!inst) return;
    const valid = (ids || []).filter((id) => id && id !== "__region");
    requestAnimationFrame(() => {
      try {
        if (valid.length) inst.fitView({ nodes: valid.map((id) => ({ id })), duration: 550, padding: 0.4, maxZoom: 1.4 });
        else inst.fitView({ duration: 550, padding: 0.14 });
      } catch (_) { /* noop */ }
    });
  }, []);
  const neighborsOf = (id) => {
    const edges = (graphRef.current?.edges || []).filter((e) => e.group !== "detail");
    const ids = new Set([id]);
    edges.forEach((e) => { if (e.source === id || e.target === id || e.mid_to === id) { ids.add(e.source); ids.add(e.target); if (e.mid_to) ids.add(e.mid_to); } });
    return [...ids];
  };

  const roleById = (id) => { const n = (graphRef.current?.nodes || []).find((x) => x.id === id); return n ? roleOf(n) : null; };
  const nodeLabel = (id) => (graphRef.current?.nodes || []).find((n) => n.id === id)?.label || id;

  // trace ripple from a node (returns lit node + edge sets)
  const trace = (id, cand) => {
    const litN = new Set([id]); const litE = new Set();
    const role = roleById(id);
    if (role === "strategy") {
      const q = [id]; const seen = new Set([id]);
      while (q.length) {
        const cur = q.shift();
        cand.filter((e) => e.source === cur).forEach((e) => {
          litE.add(e.id); litN.add(e.target); if (e.mid_to) litN.add(e.mid_to);
          // changes_link: also light the causal edge it acts on
          if (e.group === "changes_link") cand.filter((c) => c.group === "force" && ((c.source === e.target && c.target === e.mid_to) || (c.source === e.mid_to && c.target === e.target))).forEach((c) => litE.add(c.id));
          if (roleById(e.target) === "strategy" && !seen.has(e.target)) { seen.add(e.target); q.push(e.target); }
        });
      }
      cand.filter((e) => e.target === id && roleById(e.source) === "strategy").forEach((e) => { litE.add(e.id); litN.add(e.source); });
    } else {
      cand.filter((e) => e.source === id || e.target === id || e.mid_to === id).forEach((e) => { litE.add(e.id); litN.add(e.source); litN.add(e.target); if (e.mid_to) litN.add(e.mid_to); });
    }
    return { litN, litE };
  };

  const rebuild = useCallback((focus = focusRef.current, hover = hoverRef.current) => {
    const g = graphRef.current || {};
    const { pos, tier, region } = layoutRef.current;
    const role = (id) => { const n = (g.nodes || []).find((x) => x.id === id); return n ? roleOf(n) : null; };
    const visN = (g.nodes || []).filter((n) => roleOf(n) !== "subproblem");
    const visIds = new Set(visN.map((n) => n.id));
    const cand = (g.edges || []).filter((e) => e.group !== "detail" && visIds.has(e.source) && visIds.has(e.target) && role(e.source) !== "subproblem" && role(e.target) !== "subproblem");

    const activeNode = (focus?.type === "node" && focus.id) || hover || null;
    const activeEdge = focus?.type === "edge" ? focus.id : null;
    const active = activeNode || activeEdge;
    let litN = null, litE = null;
    if (activeEdge) { const e = cand.find((x) => x.id === activeEdge); litE = new Set(e ? [e.id] : []); litN = new Set(e ? [e.source, e.target, e.mid_to].filter(Boolean) : []); }
    else if (activeNode) ({ litN, litE } = trace(activeNode, cand));

    const rf = [];
    if (region) rf.push({ id: "__region", type: "sys", position: { x: region.x, y: region.y }, draggable: false, selectable: false, zIndex: 0, data: { raw: { size: region.size }, role: "region" } });
    visN.forEach((n) => rf.push({ id: n.id, type: "sys", position: pos[n.id] || { x: n.x ?? 0, y: n.y ?? 0 }, draggable: true, zIndex: 1, data: { raw: n, role: roleOf(n), tier: tier[n.id], dim: litN ? !litN.has(n.id) : false, active: n.id === activeNode } }));
    setNodes(rf);

    setEdges(cand.map((e) => {
      const sp = edgeSpec(e, role);
      const lit = litE ? litE.has(e.id) : false;
      const opacity = active ? (lit ? 1 : 0.14) : sp.base;
      const showMarker = !active || lit;
      const showBadge = !!sp.sign && (!active || lit);
      return {
        id: e.id, source: e.source, target: e.target, type: "floating",
        data: { color: sp.color, sign: sp.sign, filled: sp.filled, showBadge, midTo: e.mid_to || undefined },
        style: { stroke: sp.color, strokeWidth: lit ? sp.w + 0.8 : sp.w, opacity, strokeDasharray: sp.dash },
        markerEnd: showMarker ? { type: MarkerType.ArrowClosed, color: sp.color, width: 13, height: 13 } : undefined,
        zIndex: lit ? 5 : 1,
      };
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setNodes, setEdges]);

  useEffect(() => {
    graphRef.current = systemsMap || { nodes: [], edges: [] };
    layoutRef.current = computeLayout(graphRef.current);
    focusRef.current = null; hoverRef.current = null; setSel(null);
    rebuild(null, null);
    /* eslint-disable-next-line */
  }, [systemsMap]);

  const persist = useCallback(() => { api.saveSystemsMap(sessionId, graphRef.current).catch(() => {}); }, [sessionId]);
  const commitPositions = () => {
    const byId = Object.fromEntries(nodes.filter((n) => n.id !== "__region").map((n) => [n.id, n.position]));
    Object.entries(byId).forEach(([id, p]) => { layoutRef.current.pos[id] = { x: Math.round(p.x), y: Math.round(p.y) }; });
    graphRef.current = { ...graphRef.current, nodes: graphRef.current.nodes.map((n) => byId[n.id] ? { ...n, x: Math.round(byId[n.id].x), y: Math.round(byId[n.id].y) } : n) };
    persist();
  };

  const setFocus = (f) => { focusRef.current = f; rebuild(f, hoverRef.current); };
  const setHover = (id) => { if (id === "__region") id = null; if (hoverRef.current === id) return; hoverRef.current = id; rebuild(focusRef.current, id); };
  const onNodeClick = (_, n) => { if (n.id === "__region") return; setSel({ type: "node", id: n.id }); setFocus({ type: "node", id: n.id }); if (fill) focusView(neighborsOf(n.id)); };
  const onEdgeClick = (_, e) => { setSel({ type: "edge", id: e.id }); setFocus({ type: "edge", id: e.id }); const raw = (graphRef.current?.edges || []).find((x) => x.id === e.id); if (fill) focusView(raw ? [raw.source, raw.target, raw.mid_to] : null); };
  const clearSel = () => { setSel(null); setFocus(null); if (fill) focusView(null); };

  const mutateEdge = (id, fn, del = false) => {
    graphRef.current = { ...graphRef.current, edges: del ? graphRef.current.edges.filter((e) => e.id !== id) : graphRef.current.edges.map((e) => e.id === id ? fn(e) : e) };
    persist(); rebuild(del ? null : focusRef.current, hoverRef.current);
    if (del) setSel(null);
  };
  const onConnect = useCallback((c) => {
    const id = `e-${Date.now()}`;
    graphRef.current = { ...graphRef.current, edges: [...graphRef.current.edges, { id, source: c.source, target: c.target, relation: "better", why: "", confidence: "moderate", provisional: false, group: "custom", label: "" }] };
    persist(); rebuild(null, hoverRef.current); setSel({ type: "edge", id }); toast.success("Relationship added");
  }, [persist, rebuild]);

  const selEdgeRaw = sel?.type === "edge" ? (graphRef.current.edges || []).find((e) => e.id === sel.id) : null;
  const selNodeRaw = sel?.type === "node" ? (graphRef.current.nodes || []).find((n) => n.id === sel.id) : null;
  const selRole = selNodeRaw ? roleOf(selNodeRaw) : null;
  const selTier = selNodeRaw ? layoutRef.current.tier[selNodeRaw.id] : null;
  const confIdx = selEdgeRaw ? Math.max(0, CONF.indexOf(selEdgeRaw.confidence)) : 0;
  const selIsChange = selEdgeRaw?.group === "changes_link";

  const askWhy = async () => {
    if (!selEdgeRaw) return;
    setExplaining(true);
    try { const r = await api.explainRelationship(sessionId, { source_label: nodeLabel(selEdgeRaw.source), relation: legacy(selEdgeRaw.relation), target_label: nodeLabel(selIsChange ? selEdgeRaw.mid_to : selEdgeRaw.target) }); setExplanation((m) => ({ ...m, [selEdgeRaw.id]: r.explanation })); }
    catch (e) { toast.error("Could not explain", { description: e?.response?.data?.detail || e.message }); }
    finally { setExplaining(false); }
  };

  const allEdges = graphRef.current?.edges || [];
  const subproblems = selRole === "problem" ? (selNodeRaw.problem_ids || []).map((pid) => nodeLabel(pid)) : [];
  const relatedStrategies = selRole === "problem" ? allEdges.filter((e) => e.target === selNodeRaw.id && roleById(e.source) === "strategy" && e.group !== "changes_link").map((e) => nodeLabel(e.source)) : [];
  const problemLinks = selRole === "problem" ? allEdges.filter((e) => (e.source === selNodeRaw.id || e.target === selNodeRaw.id) && roleById(e.source) === "problem" && roleById(e.target) === "problem").map((e) => ({ from: nodeLabel(e.source), to: nodeLabel(e.target), rel: legacy(e.relation), loop: e.loop, self: e.source === selNodeRaw.id })) : [];
  const stratImproves = selRole === "strategy" ? allEdges.filter((e) => e.source === selNodeRaw.id && roleById(e.target) === "problem" && e.group !== "changes_link").map((e) => ({ label: nodeLabel(e.target), why: e.label })) : [];
  const stratChanges = selRole === "strategy" ? allEdges.filter((e) => e.source === selNodeRaw.id && e.group === "changes_link").map((e) => ({ from: nodeLabel(e.target), to: nodeLabel(e.mid_to), effect: e.relation, why: e.label })) : [];
  const stratEnables = selRole === "strategy" ? allEdges.filter((e) => e.source === selNodeRaw.id && roleById(e.target) === "strategy").map((e) => nodeLabel(e.target)) : [];
  const stratAfter = selRole === "strategy" ? allEdges.filter((e) => e.target === selNodeRaw.id && roleById(e.source) === "strategy").map((e) => nodeLabel(e.source)) : [];

  return (
    <div className="relative flex" style={{ height: fill ? "100%" : 640 }} data-testid="systems-map">
      <div className="relative min-w-0 flex-1">
        <ReactFlow nodes={nodes} edges={edges} nodeTypes={nodeTypes} edgeTypes={edgeTypes} connectionMode={ConnectionMode.Loose}
          onInit={(inst) => { rfiRef.current = inst; }}
          onNodesChange={onNodesChange} onEdgesChange={onEdgesChange}
          onNodeDragStop={commitPositions} onConnect={onConnect} onNodeClick={onNodeClick} onEdgeClick={onEdgeClick} onPaneClick={clearSel}
          onNodeMouseEnter={(_, n) => setHover(n.id)} onNodeMouseLeave={() => setHover(null)}
          zoomOnScroll={fill} zoomOnPinch zoomOnDoubleClick panOnDrag panOnScroll={false} preventScrolling={fill}
          nodesDraggable={fill} nodesConnectable={fill}
          fitView fitViewOptions={{ padding: 0.14 }} minZoom={0.1} proOptions={{ hideAttribution: true }}>
          <Background gap={26} size={1} color="#c9cdd6" style={{ opacity: 0.4 }} />
          {fill && (
            <Panel position="top-left">
              <button data-testid="map-recenter-btn" onClick={() => focusView(null)} title="Fit the whole map to the screen"
                className="inline-flex items-center gap-1.5 rounded-full border border-border bg-card px-3.5 py-2 text-xs font-semibold shadow-md transition-colors hover:bg-muted">
                <FrameCorners size={15} weight="bold" /> Recenter
              </button>
            </Panel>
          )}
          <Controls showInteractive={false} position="bottom-left" />
          {fill && <MiniMap pannable zoomable nodeColor={(n) => (n.data?.role === "region" ? "transparent" : n.data?.role === "strategy" ? (n.data?.tier === "primary" ? "#0F62FE" : "#93b4fb") : "#0d5c63")} />}
        </ReactFlow>

      {(selEdgeRaw || selNodeRaw) && (
        <div data-testid="map-inspector" className="thin-scroll absolute right-0 top-0 z-10 flex h-full w-[340px] flex-col overflow-y-auto border-l border-border bg-card p-4 shadow-lg">
          <div className="flex items-start justify-between gap-2">
            <span className="font-mono text-[10px] font-semibold uppercase tracking-widest text-muted-foreground">{selEdgeRaw ? (selIsChange ? "Structural change" : "Relationship") : selRole === "strategy" ? `${selTier === "primary" ? "Primary" : "Secondary"} intervention` : "Problem"}</span>
            <button data-testid="close-inspector" onClick={clearSel} className="text-muted-foreground hover:text-foreground"><X size={16} /></button>
          </div>

          {selEdgeRaw && (
            <div className="mt-2">
              {selIsChange
                ? <p className="text-[14px] leading-snug"><strong>{nodeLabel(selEdgeRaw.source)}</strong> <span style={{ color: CHANGE }}>{selEdgeRaw.relation === "strengthens" ? "strengthens" : "weakens"}</span> the link <strong>{nodeLabel(selEdgeRaw.target)} → {nodeLabel(selEdgeRaw.mid_to)}</strong></p>
                : selEdgeRaw.label
                  ? <p className="text-[14px] leading-snug"><strong>{nodeLabel(selEdgeRaw.source)}</strong> <span style={{ color: relColor(selEdgeRaw.relation) }}>{selEdgeRaw.label}</span> <strong>{nodeLabel(selEdgeRaw.target)}</strong></p>
                  : <p className="text-[14px] leading-snug"><RelSentence subject={nodeLabel(selEdgeRaw.source)} target={nodeLabel(selEdgeRaw.target)} rel={selEdgeRaw.relation} boldSubject boldTarget /></p>}
              <div className="mt-3 space-y-2 border-t border-border pt-2">
                <div>
                  <span className="font-mono text-[9px] uppercase tracking-widest text-muted-foreground">{selIsChange ? "How it changes the structure" : "Why this happens"}</span>
                  <input data-testid="mechanism-input" key={selEdgeRaw.id} defaultValue={selEdgeRaw.label || ""} placeholder="why? (a few words)" onBlur={(e) => { if (e.target.value !== (selEdgeRaw.label || "")) mutateEdge(selEdgeRaw.id, (ed) => ({ ...ed, label: e.target.value })); }} className="mt-0.5 w-full rounded-none border border-border bg-background px-2 py-1 text-[12px] outline-none focus:border-foreground" />
                  {selEdgeRaw.why && <p className="mt-1 text-[11.5px] italic leading-snug text-muted-foreground">{selEdgeRaw.why}</p>}
                </div>
                <div className="flex items-center gap-2">
                  <span className="font-mono text-[9px] uppercase tracking-widest text-muted-foreground">Confidence</span>
                  <span className="flex items-center gap-0.5">{[0, 1, 2].map((i) => <span key={i} className="inline-block h-1.5 w-4" style={{ background: i <= confIdx ? (selIsChange ? CHANGE : relColor(selEdgeRaw.relation)) : "hsl(var(--muted))" }} />)}</span>
                  <span className="text-[11px] capitalize text-foreground/80">{selEdgeRaw.confidence}</span>
                </div>
                <p className="text-[11px] leading-snug text-muted-foreground"><span className="font-mono text-[9px] uppercase tracking-widest">Evidence · </span>{selEdgeRaw.provisional ? "A plausible link inferred from the situation — not yet directly evidenced." : "Consistent with your account of the situation."}</p>
              </div>
              {!selIsChange && <div className="mt-3 flex flex-wrap gap-1">
                {RELATIONS.map((r) => <button key={r} data-testid={`rel-${r}`} onClick={() => mutateEdge(selEdgeRaw.id, (e) => ({ ...e, relation: r }))} className={`rounded-none border px-2.5 py-0.5 text-[10.5px] ${legacy(selEdgeRaw.relation) === r ? "text-white" : "hover:bg-muted"}`} style={legacy(selEdgeRaw.relation) === r ? { background: REL[r].color, borderColor: REL[r].color } : {}}>{REL[r].text}</button>)}
              </div>}
              <div className="mt-2.5 flex flex-wrap gap-1.5">
                {!selIsChange && <button data-testid="reverse-edge" onClick={() => mutateEdge(selEdgeRaw.id, (e) => ({ ...e, source: e.target, target: e.source }))} className="inline-flex items-center gap-1 rounded-none border border-border px-2 py-1 text-[11px] hover:bg-muted"><ArrowsClockwise size={12} /> Reverse</button>}
                <button data-testid="cycle-confidence" onClick={() => mutateEdge(selEdgeRaw.id, (e) => ({ ...e, confidence: CONF[(CONF.indexOf(e.confidence) + 1) % 3] }))} className="inline-flex items-center gap-1 rounded-none border border-border px-2 py-1 text-[11px] hover:bg-muted">Set: {selEdgeRaw.confidence}</button>
                <button data-testid="toggle-provisional" onClick={() => mutateEdge(selEdgeRaw.id, (e) => ({ ...e, provisional: !e.provisional }))} className="inline-flex items-center gap-1 rounded-none border border-border px-2 py-1 text-[11px] hover:bg-muted"><WarningCircle size={12} /> {selEdgeRaw.provisional ? "Uncertain" : "Confident"}</button>
                <button data-testid="delete-edge" onClick={() => mutateEdge(selEdgeRaw.id, null, true)} className="inline-flex items-center gap-1 rounded-none border border-destructive/40 px-2 py-1 text-[11px] text-destructive hover:bg-destructive/5"><Trash size={12} /> Remove</button>
              </div>
              <button data-testid="ask-why" onClick={askWhy} disabled={explaining} className="mt-2.5 inline-flex items-center gap-1.5 rounded-none border border-primary bg-primary px-3 py-1.5 text-[11px] font-medium text-primary-foreground hover:bg-primary/85 disabled:opacity-50">{explaining ? <CircleNotch size={12} className="animate-spin" /> : <Question size={12} weight="bold" />} Explain in more depth</button>
              {explanation[selEdgeRaw.id] && <p className="mt-2 rounded-none bg-muted p-2 text-[12px] leading-relaxed">{explanation[selEdgeRaw.id]}</p>}
            </div>
          )}

          {selNodeRaw && selRole === "problem" && (
            <div className="mt-2">
              <p className="font-serif text-[18px] font-semibold leading-tight">{selNodeRaw.label}</p>
              {selNodeRaw.summary && <p className="mt-1.5 text-[13px] leading-relaxed text-muted-foreground">{selNodeRaw.summary}</p>}
              <div className="mt-3 space-y-2">
                {subproblems.length > 0 && <Section title="Underlying problems"><ul className="space-y-0.5">{subproblems.map((p, i) => <li key={i} className="text-[12px] leading-snug text-foreground/80">{i + 1}. {p}</li>)}</ul></Section>}
                {problemLinks.length > 0 && <Section title="How this problem connects to others" defaultOpen><ul className="space-y-1.5">{problemLinks.map((l, i) => <li key={i} className="text-[12px] leading-snug">{i + 1}. {l.self ? <RelSentence subject={selNodeRaw.label} target={l.to} rel={l.rel} boldTarget /> : <RelSentence subject={l.from} target={selNodeRaw.label} rel={l.rel} boldSubject />}</li>)}</ul></Section>}
                {relatedStrategies.length > 0 && <Section plain title={`Interventions that address “${selNodeRaw.label}”`} defaultOpen><ul className="space-y-0.5">{relatedStrategies.map((s, i) => <li key={i} className="text-[12px] leading-snug text-[#0b57d0] dark:text-[#78A9FF]">{i + 1}. {s}</li>)}</ul></Section>}
              </div>
            </div>
          )}

          {selNodeRaw && selRole === "strategy" && (
            <div className="mt-2">
              <p className="font-serif text-[18px] font-semibold leading-tight text-[#0b57d0] dark:text-[#78A9FF]">{selNodeRaw.label}</p>
              <p className="mt-1 text-[11.5px] leading-snug text-muted-foreground">{selTier === "primary" ? "Do this first. It changes the situation now and eases several problems at once." : "Do this after the first moves have changed the situation — it only works once they're in place."}</p>
              {selNodeRaw.summary && <p className="mt-1.5 text-[13px] leading-relaxed text-foreground/90">{selNodeRaw.summary}</p>}
              <div className="mt-3 space-y-2">
                {stratImproves.length > 0 && <Section title="Problems it improves" defaultOpen><ul className="space-y-1">{stratImproves.map((s, i) => <li key={i} className="text-[12px] leading-snug">{i + 1}. <span className="font-medium">{s.label}</span>{s.why ? <span className="text-muted-foreground"> — {s.why}</span> : null}</li>)}</ul></Section>}
                {stratChanges.length > 0 && <Section title="Changes it makes to the system" defaultOpen><ul className="space-y-1">{stratChanges.map((s, i) => <li key={i} className="text-[12px] leading-snug">{i + 1}. <span style={{ color: CHANGE }}>{s.effect === "strengthens" ? "Strengthens" : "Weakens"}</span> how <span className="font-medium">{s.from}</span> affects <span className="font-medium">{s.to}</span>{s.why ? <span className="text-muted-foreground"> ({s.why})</span> : null}</li>)}</ul></Section>}
                {stratEnables.length > 0 && <Section plain title="Makes these interventions possible next" defaultOpen><ul className="space-y-0.5">{stratEnables.map((s, i) => <li key={i} className="text-[12px] leading-snug text-[#0b57d0] dark:text-[#78A9FF]">{i + 1}. {s}</li>)}</ul></Section>}
                {stratAfter.length > 0 && <Section plain title="Only becomes effective after these"><ul className="space-y-0.5">{stratAfter.map((s, i) => <li key={i} className="text-[12px] leading-snug text-muted-foreground">{i + 1}. {s}</li>)}</ul></Section>}
              </div>
            </div>
          )}
        </div>
      )}
      </div>
    </div>
  );
}
